import {
  createContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { createTheme } from "@mui/material/styles";
import Cache from "./utils/Cache";

// shared font stack (Inter self-hosted via @fontsource/inter)
const fontFamily = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "sans-serif",
].join(",");

export const DARK_PALETTES = [
  { id: "dark", label: "Slate", swatch: "#1E2530", accent: "#7AB0F8" },
  { id: "midnight", label: "Midnight", swatch: "#0B0F14", accent: "#5AA9FF" },
  { id: "carbon", label: "Carbon", swatch: "#1A1A1A", accent: "#E2A86B" },
  {
    id: "charcoal",
    label: "Charcoal grey",
    swatch: "#26292E",
    accent: "#9FC0DC",
  },
  { id: "graphite", label: "Graphite", swatch: "#1C1E22", accent: "#C3CBD4" },
  { id: "gunmetal", label: "Gunmetal", swatch: "#222831", accent: "#7FC8D9" },
];

export const DEFAULT_DARK_PALETTE = "midnight";

// per-palette design tokens, keyed by the `--*` custom properties from the
// design handoff (design_handoff_dark_theme/README.md, "Design tokens")
const DARK_TOKENS = {
  dark: {
    bg: "#1E2530",
    surface: "#262F3C",
    surface2: "#2C3644",
    input: "#222B37",
    rowHover: "#2C3644",
    openRow: "rgba(122,176,248,.10)",
    ink: "#F2F5F9",
    body: "#D3DAE3",
    sec: "#98A3B3",
    muted: "#6F7B8B",
    disFg: "#5F6B7A",
    border: "#3A4555",
    divider: "#333D4C",
    ctl: "#46536A",
    railEdge: "#3A4555",
    link: "#7AB0F8",
    btn: "#2B6FD6",
    btnHover: "#3A82E8",
    tint: "rgba(122,176,248,.14)",
    selected: "rgba(122,176,248,.16)",
    okBg: "rgba(74,222,128,.14)",
    okFg: "#86EFAC",
    errBg: "rgba(248,113,113,.16)",
    errFg: "#FCA5A5",
    warnBg: "rgba(251,191,36,.16)",
    warnFg: "#FCD34D",
    neuBg: "rgba(148,163,184,.18)",
    neuFg: "#CBD5E1",
    infoBg: "rgba(96,165,250,.16)",
    infoFg: "#93C5FD",
    purBg: "rgba(167,139,250,.16)",
    purFg: "#C4B5FD",
    okText: "#4ADE80",
    errText: "#F87171",
    warnText: "#FBBF24",
    disBg: "#2A333F",
    disBorder: "#3A4555",
    scrim: "rgba(5,10,18,.62)",
    shadow: "0 8px 24px rgba(0,0,0,.5)",
    drawerShadow: "-8px 0 32px rgba(0,0,0,.55)",
    switchTrack: "#3A82E8",
    switchOff: "#46536A",
    aws: "#FFFFFF",
  },
  midnight: {
    bg: "#0B0F14",
    surface: "#121821",
    surface2: "#18202B",
    input: "#0F141C",
    rowHover: "#18202B",
    openRow: "rgba(90,169,255,.10)",
    ink: "#F4F7FB",
    body: "#CBD5E1",
    sec: "#8A97A8",
    muted: "#61707F",
    disFg: "#55616F",
    border: "#2A3441",
    divider: "#212A36",
    ctl: "#38445A",
    railEdge: "#2A3441",
    link: "#5AA9FF",
    btn: "#1F6FE0",
    btnHover: "#2F84F5",
    tint: "rgba(90,169,255,.14)",
    selected: "rgba(90,169,255,.18)",
    okBg: "rgba(52,211,153,.14)",
    okFg: "#6EE7B7",
    errBg: "rgba(244,63,94,.16)",
    errFg: "#FDA4AF",
    warnBg: "rgba(245,158,11,.16)",
    warnFg: "#FCD34D",
    neuBg: "rgba(148,163,184,.16)",
    neuFg: "#C3CEDC",
    infoBg: "rgba(90,169,255,.16)",
    infoFg: "#93C5FD",
    purBg: "rgba(167,139,250,.16)",
    purFg: "#C4B5FD",
    okText: "#34D399",
    errText: "#FB7185",
    warnText: "#F59E0B",
    disBg: "#161D26",
    disBorder: "#2A3441",
    scrim: "rgba(0,0,0,.7)",
    shadow: "0 8px 28px rgba(0,0,0,.65)",
    drawerShadow: "-8px 0 32px rgba(0,0,0,.7)",
    switchTrack: "#2F84F5",
    switchOff: "#38445A",
    aws: "#FFFFFF",
  },
  carbon: {
    bg: "#1A1A1A",
    surface: "#212121",
    surface2: "#272727",
    input: "#1C1C1C",
    rowHover: "#272727",
    openRow: "rgba(226,168,107,.09)",
    ink: "#F5F5F4",
    body: "#D6D3D1",
    sec: "#A1A1A0",
    muted: "#78716C",
    disFg: "#6B6B6B",
    border: "#343434",
    divider: "#2C2C2C",
    ctl: "#454545",
    railEdge: "#343434",
    link: "#E2A86B",
    btn: "#C97F3C",
    btnHover: "#DB9151",
    tint: "rgba(226,168,107,.14)",
    selected: "rgba(226,168,107,.16)",
    okBg: "rgba(132,204,22,.14)",
    okFg: "#BEF264",
    errBg: "rgba(248,113,113,.16)",
    errFg: "#FCA5A5",
    warnBg: "rgba(250,204,21,.16)",
    warnFg: "#FDE047",
    neuBg: "rgba(168,162,158,.16)",
    neuFg: "#D6D3D1",
    infoBg: "rgba(226,168,107,.16)",
    infoFg: "#F0C08A",
    purBg: "rgba(216,180,254,.16)",
    purFg: "#E9D5FF",
    okText: "#A3E635",
    errText: "#F87171",
    warnText: "#FACC15",
    disBg: "#242424",
    disBorder: "#343434",
    scrim: "rgba(0,0,0,.66)",
    shadow: "0 8px 24px rgba(0,0,0,.6)",
    drawerShadow: "-8px 0 32px rgba(0,0,0,.66)",
    switchTrack: "#DB9151",
    switchOff: "#454545",
    aws: "#FFFFFF",
  },
  charcoal: {
    bg: "#26292E",
    surface: "#2E3237",
    surface2: "#353A40",
    input: "#23262A",
    rowHover: "#353A40",
    openRow: "rgba(159,192,220,.09)",
    ink: "#F3F4F6",
    body: "#D2D6DB",
    sec: "#9BA2AB",
    muted: "#737B85",
    disFg: "#6B727B",
    border: "#434950",
    divider: "#3B4046",
    ctl: "#4E555E",
    railEdge: "#434950",
    link: "#9FC0DC",
    btn: "#5E7D99",
    btnHover: "#6F91AF",
    tint: "rgba(159,192,220,.13)",
    selected: "rgba(159,192,220,.17)",
    okBg: "rgba(110,231,183,.13)",
    okFg: "#A7F3D0",
    errBg: "rgba(248,113,113,.15)",
    errFg: "#FCA5A5",
    warnBg: "rgba(251,191,36,.15)",
    warnFg: "#FCD34D",
    neuBg: "rgba(156,163,175,.18)",
    neuFg: "#D2D6DB",
    infoBg: "rgba(159,192,220,.16)",
    infoFg: "#BBD5EB",
    purBg: "rgba(196,181,253,.15)",
    purFg: "#DDD6FE",
    okText: "#6EE7B7",
    errText: "#F87171",
    warnText: "#FBBF24",
    disBg: "#2F3338",
    disBorder: "#434950",
    scrim: "rgba(10,12,14,.62)",
    shadow: "0 8px 24px rgba(0,0,0,.5)",
    drawerShadow: "-8px 0 32px rgba(0,0,0,.55)",
    switchTrack: "#6F91AF",
    switchOff: "#4E555E",
    aws: "#FFFFFF",
  },
  graphite: {
    bg: "#1C1E22",
    surface: "#24272C",
    surface2: "#2B2F35",
    input: "#191B1F",
    rowHover: "#2B2F35",
    openRow: "rgba(195,203,212,.07)",
    ink: "#F2F4F7",
    body: "#CFD4DA",
    sec: "#959CA6",
    muted: "#6D747E",
    disFg: "#646B75",
    border: "#3A3F47",
    divider: "#32363D",
    ctl: "#454B54",
    railEdge: "#3A3F47",
    link: "#C3CBD4",
    btn: "#4C5763",
    btnHover: "#5C6875",
    tint: "rgba(195,203,212,.11)",
    selected: "rgba(195,203,212,.15)",
    okBg: "rgba(110,231,183,.13)",
    okFg: "#A7F3D0",
    errBg: "rgba(248,113,113,.15)",
    errFg: "#FCA5A5",
    warnBg: "rgba(251,191,36,.15)",
    warnFg: "#FCD34D",
    neuBg: "rgba(156,163,175,.17)",
    neuFg: "#CFD4DA",
    infoBg: "rgba(195,203,212,.15)",
    infoFg: "#DDE2E8",
    purBg: "rgba(196,181,253,.15)",
    purFg: "#DDD6FE",
    okText: "#6EE7B7",
    errText: "#F87171",
    warnText: "#FBBF24",
    disBg: "#25282D",
    disBorder: "#3A3F47",
    scrim: "rgba(8,9,11,.64)",
    shadow: "0 8px 24px rgba(0,0,0,.55)",
    drawerShadow: "-8px 0 32px rgba(0,0,0,.6)",
    switchTrack: "#5C6875",
    switchOff: "#454B54",
    aws: "#FFFFFF",
  },
  gunmetal: {
    bg: "#222831",
    surface: "#2A313B",
    surface2: "#313945",
    input: "#1E242C",
    rowHover: "#313945",
    openRow: "rgba(127,200,217,.08)",
    ink: "#F1F4F8",
    body: "#CED5DE",
    sec: "#939DAA",
    muted: "#6C7683",
    disFg: "#69727F",
    border: "#404A58",
    divider: "#39424E",
    ctl: "#4A5464",
    railEdge: "#404A58",
    link: "#7FC8D9",
    btn: "#3D7E90",
    btnHover: "#4A93A7",
    tint: "rgba(127,200,217,.12)",
    selected: "rgba(127,200,217,.16)",
    okBg: "rgba(110,231,183,.13)",
    okFg: "#A7F3D0",
    errBg: "rgba(248,113,113,.15)",
    errFg: "#FCA5A5",
    warnBg: "rgba(251,191,36,.15)",
    warnFg: "#FCD34D",
    neuBg: "rgba(148,163,184,.18)",
    neuFg: "#CED5DE",
    infoBg: "rgba(127,200,217,.16)",
    infoFg: "#A5DCE8",
    purBg: "rgba(196,181,253,.15)",
    purFg: "#DDD6FE",
    okText: "#6EE7B7",
    errText: "#F87171",
    warnText: "#FBBF24",
    disBg: "#2B313A",
    disBorder: "#404A58",
    scrim: "rgba(7,10,14,.64)",
    shadow: "0 8px 24px rgba(0,0,0,.52)",
    drawerShadow: "-8px 0 32px rgba(0,0,0,.58)",
    switchTrack: "#4A93A7",
    switchOff: "#4A5464",
    aws: "#FFFFFF",
  },
};

const LIGHT_TOKENS = {
  bg: "#f9fafb",
  surface: "#ffffff",
  surface2: "#f9fafb",
  input: "#ffffff",
  rowHover: "#f9fafb",
  openRow: "#F0F6FE",
  ink: "#111827",
  body: "#374151",
  sec: "#6b7280",
  muted: "#9ca3af",
  disFg: "#A8B0B8",
  border: "#e5e7eb",
  divider: "#f3f4f6",
  ctl: "#D6DAE0",
  railEdge: "#E2E5E9",
  link: "#125cc7",
  btn: "#1059D0",
  btnHover: "#0E4CB0",
  tint: "#E7F0FD",
  selected: "#E3EFFB",
  okBg: "#EAFAEA",
  okFg: "#196426",
  errBg: "#FFEBEB",
  errFg: "#B32626",
  warnBg: "#FFF3E0",
  warnFg: "#9D620B",
  neuBg: "#F3F4F6",
  neuFg: "#374151",
  infoBg: "#E7F0FD",
  infoFg: "#125CC7",
  purBg: "#F5F3FF",
  purFg: "#6D28D9",
  okText: "#196426",
  errText: "#B32626",
  warnText: "#9D620B",
  disBg: "#F4F5F7",
  disBorder: "#E2E5E9",
  scrim: "rgba(16,24,40,.45)",
  shadow: "0 6px 20px rgba(16,24,40,.14)",
  drawerShadow: "-8px 0 32px rgba(16,24,40,.18)",
  switchTrack: "#B7C4D8",
  switchOff: "#E5E7EB",
  aws: "#252F3E",
};

const isDarkPaletteId = (id) =>
  Object.prototype.hasOwnProperty.call(DARK_TOKENS, id);

// color design tokens export — keyed by palette id: "light" or one of the
// six dark palette ids (dark, midnight, carbon, charcoal, graphite, gunmetal)
export const tokens = (palette) => {
  const dark = isDarkPaletteId(palette);
  const t = dark ? DARK_TOKENS[palette] : LIGHT_TOKENS;
  return {
    // lets page-local tone tables branch without re-deriving the palette id
    isDark: dark,
    ...(dark
      ? {
          // The handoff's "Maps to" column: the dark palettes drive the same
          // scale keys the components already read, so every existing
          // colors.grey/primary/blue reference becomes palette-aware.
          grey: {
            100: t.ink,
            200: t.ink,
            300: t.muted,
            400: t.sec,
            500: t.sec,
            600: t.muted,
            700: t.body,
            800: t.border,
            900: t.divider,
          },
          primary: {
            100: t.ink,
            200: t.ink,
            300: t.body,
            400: t.surface2,
            500: t.border,
            600: t.surface2,
            700: t.surface,
            800: t.bg,
            900: t.link,
          },
          green: {
            100: t.okBg,
            200: t.okBg,
            300: t.okText,
            400: t.okText,
            500: t.okText,
            600: t.btn,
            700: t.btnHover,
            800: t.btnHover,
            900: t.okBg,
          },
          red: {
            100: t.errBg,
            200: t.errFg,
            300: t.errText,
            400: t.errText,
            500: t.errText,
            600: t.errText,
            700: t.errText,
            800: t.errText,
            900: t.errBg,
          },
          blue: {
            100: t.tint,
            200: t.tint,
            300: t.link,
            400: t.link,
            500: t.link,
            600: t.btn,
            700: t.btnHover,
            800: t.btnHover,
            900: t.btnHover,
          },
        }
      : {
          // Tessell light palette (keys preserved; hex remapped to portal tokens)
          grey: {
            100: "#111827", // primary text
            200: "#1f2937",
            300: "#9ca3af", // sidebar section headers / muted labels
            400: "#6b7280", // muted text
            500: "#6b7280",
            600: "#9ca3af", // disabled button base
            700: "#374151", // secondary text
            800: "#e5e7eb", // border
            900: "#f3f4f6", // subtle divider
          },
          primary: {
            100: "#111827",
            200: "#1f2937",
            300: "#374151",
            400: "#eff2f7",
            500: "#e5e7eb",
            600: "#f9fafb", // table header bg / card base
            700: "#ffffff", // table body + sidebar surface
            800: "#f9fafb", // app background
            900: "#125cc7", // palette.primary.main -> brand blue
          },
          green: {
            100: "#eafaea",
            200: "#c9f0ce",
            300: "#196426", // success text
            400: "#1f8f4b",
            500: "#2f833d",
            600: "#125cc7", // default CTA -> brand blue
            700: "#094396", // CTA hover
            800: "#08326f",
            900: "#eafaea",
          },
          red: {
            100: "#ffebeb",
            200: "#f89191",
            300: "#d33b3b",
            400: "#d73d3d",
            500: "#b32626",
            600: "#9f2222",
            700: "#7f1b1b",
            800: "#5a1313",
            900: "#fdecec",
          },
          blue: {
            100: "#e7f0fd", // active tint
            200: "#cfe2fc",
            300: "#68a3f7", // hover
            400: "#4d7aff",
            500: "#125cc7", // brand
            600: "#0f4fb0",
            700: "#094396", // pressed
            800: "#08326f",
            900: "#052046",
          },
        }),
    // design-handoff surface/ink/line/status tokens, shared shape across
    // light and every dark palette (design_handoff_dark_theme/README.md)
    bg: t.bg,
    surface: t.surface,
    surface2: t.surface2,
    input: t.input,
    rowHover: t.rowHover,
    openRow: t.openRow,
    ink: t.ink,
    body: t.body,
    sec: t.sec,
    muted: t.muted,
    disFg: t.disFg,
    border: t.border,
    divider: t.divider,
    ctl: t.ctl,
    railEdge: t.railEdge,
    link: t.link,
    btn: t.btn,
    btnHover: t.btnHover,
    tint: t.tint,
    selected: t.selected,
    okBg: t.okBg,
    okFg: t.okFg,
    errBg: t.errBg,
    errFg: t.errFg,
    warnBg: t.warnBg,
    warnFg: t.warnFg,
    neuBg: t.neuBg,
    neuFg: t.neuFg,
    infoBg: t.infoBg,
    infoFg: t.infoFg,
    purBg: t.purBg,
    purFg: t.purFg,
    okText: t.okText,
    errText: t.errText,
    warnText: t.warnText,
    disBg: t.disBg,
    disBorder: t.disBorder,
    scrim: t.scrim,
    shadow: t.shadow,
    drawerShadow: t.drawerShadow,
    switchTrack: t.switchTrack,
    switchOff: t.switchOff,
    aws: t.aws,
    // Handoff gap: badgeStyles' QA / stage / prod chips carry no token of their
    // own in the README table. Light keeps today's exact hexes so the refactor
    // is a no-op; dark aliases the nearest pair the palette already defines.
    qaBg: dark ? t.infoBg : "#e7f0fd",
    qaFg: dark ? t.infoFg : "#094396",
    stageBg: dark ? t.purBg : "#cfe2fc",
    stageFg: dark ? t.purFg : "#094396",
    prodBg: dark ? t.okBg : "#d1fae5",
    prodFg: dark ? t.okFg : "#065f46",
    // Handoff gap: the outlined-control tones in buttonStyles sit between
    // --body and --ink and have no token in the README table.
    ctlInk: dark ? t.body : "#3A4552",
    ctlInkHover: dark ? t.ink : "#1A2027",
    ctlHoverBg: dark ? t.rowHover : "#F2F4F7",
    actionTintBg: dark ? t.tint : "#F4F9FF",
    ctlBorderHover: dark ? t.link : "#B9D3F5",
    // Handoff gap: the sidebar's own greys and rules are a half-step off the
    // shared scale, so they keep their light hexes and take the nearest dark
    // token. navActive is the exception the README does call out: the rail's
    // active ink is --btn in light but lifts to --link on dark for contrast.
    navActive: dark ? t.link : "#1059D0",
    railMuted: dark ? t.sec : "#5A6673",
    railHeading: dark ? t.muted : "#8A949E",
    railRule: dark ? t.divider : "#EDEFF2",
    railRuleStrong: dark ? t.border : "#E8EAEE",
    onDot: dark ? t.okText : "#1B7F3B",
    // Handoff gap: three distinct light hover greys and two soft edges used by
    // the Environments list and its details drawer. They collapse onto the
    // palette's own hover/tint/border tokens on dark.
    actionHoverBg: dark ? t.tint : "#EEF2F7",
    ctlHoverBgStrong: dark ? t.rowHover : "#E6E9EE",
    menuBorder: dark ? t.border : "#E2E5E9",
    sepMuted: dark ? t.muted : "#C9CED5",
    openTint: dark ? t.selected : "#DCEAFD",
    // The env details drawer is the one drawer whose body is the page
    // background rather than --surface (see README, "Layout constants").
    drawerBody: dark ? t.bg : "#FCFCFD",
    // index.css scrollbar thumb, exposed as a CSS variable from App.js.
    scrollThumb: dark ? t.ctl : "#d1d5db",
    // Handoff gap: shared dialog / dropdown / filter-chip chrome. The README
    // covers the drawers these live in but not the components themselves.
    dialogHeaderBg: dark ? t.surface2 : "#F4F6F9",
    btnDisabledBg: dark ? t.disBg : "#A7C3EC",
    linkSoft: dark ? t.link : "#68A3F7",
    tintHover: dark ? t.tint : "#D8E7FB",
    chipNeuBg: dark ? t.neuBg : "#EEF0F3",
    chipNeuFg: dark ? t.neuFg : "#5B6470",
    // commonButtonStyles' filled CTA reads the brand blue in light; on dark it
    // takes the palette's own button pair so it stays a button, not a link.
    ctaBg: dark ? t.btn : "#125cc7",
    ctaHover: dark ? t.btnHover : "#094396",
  };
};

// mui theme settings — `palette` is "light" or one of the six dark palette ids
export const themeSettings = (palette) => {
  const colors = tokens(palette);
  const dark = isDarkPaletteId(palette);
  return {
    // the resolved palette id; components read it as `tokens(theme.paletteId)`
    // since `palette.mode` collapses all six dark palettes to "dark"
    paletteId: palette,
    palette: {
      mode: dark ? "dark" : "light",
      ...(dark
        ? {
            // palette values for dark mode
            primary: {
              // the accent, not the page background: this is what
              // `borderColor: "primary.main"` and friends resolve to
              main: colors.link,
            },
            secondary: {
              main: colors.blue[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.bg,
              paper: colors.surface,
            },
            // MUI's own grey scale, which `sx={{ bgcolor: "grey.50" }}` and
            // `borderColor: "grey.300"` resolve against. Left at MUI's defaults
            // in light; without this the stock near-white greys survive into
            // dark mode and panels render white on a dark page.
            grey: {
              50: colors.surface2,
              100: colors.divider,
              200: colors.divider,
              300: colors.ctl,
              400: colors.ctl,
              500: colors.muted,
              600: colors.sec,
              700: colors.body,
              800: colors.body,
              900: colors.ink,
            },
            text: {
              primary: colors.ink,
              secondary: colors.sec,
              disabled: colors.disFg,
            },
            divider: colors.divider,
            error: {
              main: colors.errText,
              light: colors.errFg,
              dark: colors.errText,
            },
            success: { main: colors.okText },
            warning: { main: colors.warnText },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[900],
            },
            secondary: {
              main: colors.blue[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: "#f9fafb",
            },
          }),
    },
    typography: {
      fontFamily: fontFamily,
      fontSize: 13,
      h1: {
        fontFamily,
        fontSize: 40,
        fontWeight: 700,
        letterSpacing: "-0.015em",
      },
      h2: {
        fontFamily,
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: "-0.015em",
      },
      h3: { fontFamily, fontSize: 18, fontWeight: 600 },
      h4: { fontFamily, fontSize: 16, fontWeight: 600 },
      h5: { fontFamily, fontSize: 14, fontWeight: 600 },
      h6: { fontFamily, fontSize: 12, fontWeight: 600 },
      subtitle1: { fontFamily, fontSize: 13 },
      subtitle2: { fontFamily, fontSize: 12, fontWeight: 600 },
      body1: { fontFamily, fontSize: 13 },
      body2: { fontFamily, fontSize: 12 },
      caption: { fontFamily, fontSize: 11 },
      overline: { fontFamily, fontSize: 10, letterSpacing: "0.08em" },
      button: { textTransform: "none", fontWeight: 500, fontSize: 13 },
    },
    shape: { borderRadius: 6 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: "none",
            boxShadow: "none",
            fontWeight: 500,
            fontSize: 13,
            minHeight: 28,
            width: "fit-content",
            minWidth: "unset",
            padding: "0 12px",
            gap: 6,
            "&:hover": { boxShadow: "none" },
          },
          containedPrimary: {
            color: "#fff",
            "&:hover": { backgroundColor: colors.blue[700] },
          },
          outlined: {
            borderColor: colors.grey[800],
            color: colors.grey[100],
            "&:hover": {
              borderColor: colors.grey[700],
              backgroundColor: colors.grey[900],
            },
          },
          sizeSmall: { minHeight: 26, padding: "0 10px", fontSize: 12 },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      // MUI's own default hardcodes alpha(grey[700], 0.92) + white text,
      // trusting grey[700] to stay a dark grey regardless of theme. Our dark
      // palettes repoint grey[700] at a light ink tone for text use
      // elsewhere, which turned this pill light-grey with unreadable white
      // text. Light keeps MUI's own untouched default (no override); dark
      // draws from the active palette's own elevated-surface/ink pair, so
      // each palette's tooltip matches its own chrome instead of one flat
      // grey shared by all six.
      ...(dark
        ? {
            MuiTooltip: {
              styleOverrides: {
                tooltip: {
                  backgroundColor: colors.surface2,
                  color: colors.ink,
                  border: `1px solid ${colors.border}`,
                  boxShadow: colors.shadow,
                },
              },
            },
          }
        : {}),
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 500, fontSize: 11 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 6, fontSize: 13 } },
      },
      MuiInputLabel: { styleOverrides: { root: { fontSize: 13 } } },
      MuiMenuItem: { styleOverrides: { root: { fontSize: 13 } } },
      MuiTab: {
        styleOverrides: { root: { textTransform: "none", fontWeight: 500 } },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  mode: "system",
  palette: DEFAULT_DARK_PALETTE,
  resolvedMode: "light",
  setMode: () => {},
  setPalette: () => {},
  toggleColorMode: () => {},
});

const MODE_KEY = "theme-mode";
const PALETTE_KEY = "theme-palette";
const darkSchemeQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

export const useMode = () => {
  // Default to following the OS rather than pinning light.
  const [mode, setModeState] = useState(() => Cache.get(MODE_KEY) || "system");
  const [palette, setPaletteState] = useState(
    () => Cache.get(PALETTE_KEY) || DEFAULT_DARK_PALETTE
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => darkSchemeQuery().matches
  );

  useEffect(() => {
    const query = darkSchemeQuery();
    const listener = (e) => setSystemPrefersDark(e.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);

  const setMode = useCallback((next) => {
    setModeState(next);
    Cache.set(MODE_KEY, next);
  }, []);

  const setPalette = useCallback((next) => {
    setPaletteState(next);
    Cache.set(PALETTE_KEY, next);
  }, []);

  const resolvedMode =
    mode === "system" ? (systemPrefersDark ? "dark" : "light") : mode;
  const resolvedPalette = resolvedMode === "dark" ? palette : "light";

  const colorMode = useMemo(
    () => ({
      mode,
      palette,
      resolvedMode,
      setMode,
      setPalette,
      toggleColorMode: () =>
        setMode(resolvedMode === "dark" ? "light" : "dark"),
    }),
    [mode, palette, resolvedMode, setMode, setPalette]
  );

  const theme = useMemo(
    () => createTheme(themeSettings(resolvedPalette)),
    [resolvedPalette]
  );
  return [theme, colorMode];
};
